import { useState } from 'react';
import { Form, message } from 'antd';
import dayjs from 'dayjs';
import { SOP_CONSTANTS } from '../../../constants/sopConstants';
import scheduledSopService from '../../../services/scheduledSopService';
import { generateTemplateCode } from '../../../utils/sopUtils';

export const useScheduledTemplateForm = (id, tenant, setHasExecutions, setSteps) => {
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editForm] = Form.useForm();

  // 获取模板详情
  const fetchTemplateDetail = async () => {
    if (!id || id === 'new') {
      // 创建新模板模式
      setTemplate({
        name: '',
        description: '',
        targetType: SOP_CONSTANTS.TargetType.CUSTOMER,
        triggerType: SOP_CONSTANTS.TriggerType.SCHEDULED,
        status: SOP_CONSTANTS.TemplateStatus.DRAFT,
        code: generateTemplateCode('SCHEDULED')
      });
      
      // 设置表单默认值
      editForm.setFieldsValue({
        name: '',
        description: '',
        targetType: SOP_CONSTANTS.TargetType.CUSTOMER,
        code: generateTemplateCode('SCHEDULED')
      });
      
      setSteps([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const data = await scheduledSopService.getScheduledSopDetail(id, tenant.id);
      setTemplate(data.template);
      
      // 设置表单初始值
      editForm.setFieldsValue({
        name: data.template.name,
        code: data.template.code,
        description: data.template.description,
        targetType: data.template.targetType,
        employeeIds: data.assignedEmployeeIds || [],
      });
      
      // 设置目标规则
      if (data.targetRules && data.targetRules.length > 0) {
        // 获取第一条目标规则
        const rule = data.targetRules[0];
        
        if (rule.targetType === 'GROUP') {
          // 主目标类型为客户群
          editForm.setFieldsValue({
            targetType: 'GROUP',
            targetIds: rule.targetIds || []
          });
        } else {
          // 主目标类型为客户，客户目标子类型为TAG或ALL
          editForm.setFieldsValue({
            targetType: 'CUSTOMER',
            customerTargetType: rule.targetType, // TAG或ALL
            targetIds: rule.targetType === 'TAG' ? (rule.targetIds || []) : [] // 如果是ALL类型，清空targetIds
          });
        }
      } else {
        // 设置默认值 - 主目标类型为客户，客户目标子类型为TAG
        editForm.setFieldsValue({
          targetType: 'CUSTOMER',
          customerTargetType: 'TAG',
          targetIds: []
        });
      }
      
      // 处理步骤数据
      if (data.steps && data.steps.length > 0) {
        const formattedSteps = data.steps.map(step => {
          // 转换步骤对象
          const formattedStep = {
            ...step,
            scheduledDate: step.scheduledDate ? dayjs(step.scheduledDate) : null,
            scheduledTime: step.scheduledTime ? dayjs(step.scheduledTime, 'HH:mm:ss') : null,
            originalActionParams: step.actionParams, // 保存原始的actionParams字符串
            _edited: false, // 标记为未编辑状态
          };
          
          // 处理 actionParams - 确保解析为对象
          if (formattedStep.actionParams) {
            try {
              // 检查actionParams是否为字符串
              if (typeof formattedStep.actionParams === 'string') {
                // 确保保存原始的字符串形式
                formattedStep.originalActionParams = formattedStep.actionParams;
                // 解析为对象
                formattedStep.actionParams = JSON.parse(formattedStep.actionParams);
              } else {
                // 如果已经是对象，序列化后再保存为originalActionParams
                formattedStep.originalActionParams = JSON.stringify(formattedStep.actionParams);
              }
            } catch (e) {
              console.error('解析步骤actionParams失败:', e, '原始值:', formattedStep.actionParams);
              // 即使解析失败，也要确保保留原始字符串
              if (typeof formattedStep.actionParams === 'string') {
                formattedStep.originalActionParams = formattedStep.actionParams;
              }
              formattedStep.actionParams = {}; // 设置为空对象，防止报错
            }
          } else {
            console.warn(`步骤 ${step.name} (${step.id}) 的actionParams为空`);
            formattedStep.actionParams = {}; // 确保有默认值
            formattedStep.originalActionParams = '{}'; // 也设置一个默认的原始值
          }
          
          // 根据步骤类型设置额外属性
          if (step.type === 'MESSAGE') {
            formattedStep.messageList = [];
            
            // 处理文本内容
            if (formattedStep.actionParams.text) {
              formattedStep.messageList.push({
                id: 'text-' + Date.now() + Math.random(),
                type: 'TEXT',
                params: {
                  content: formattedStep.actionParams.text
                }
              });
            }
            
            // 处理附件
            if (formattedStep.actionParams.attachments && Array.isArray(formattedStep.actionParams.attachments)) {
              formattedStep.actionParams.attachments.forEach(attachment => {
                if (attachment.msgtype === 'image') {
                  formattedStep.messageList.push({
                    id: 'image-' + Date.now() + Math.random(),
                    type: 'IMAGE',
                    params: {
                      mediaId: attachment.image.media_id || attachment.image.pic_url,
                      ossUrl: attachment.image.oss_url || ''
                    }
                  });
                } else if (attachment.msgtype === 'video') {
                  formattedStep.messageList.push({
                    id: 'video-' + Date.now() + Math.random(),
                    type: 'VIDEO',
                    params: {
                      mediaId: attachment.video.media_id,
                      ossUrl: attachment.video.oss_url || ''
                    }
                  });
                } else if (attachment.msgtype === 'file') {
                  formattedStep.messageList.push({
                    id: 'file-' + Date.now() + Math.random(),
                    type: 'FILE',
                    params: {
                      mediaId: attachment.file.media_id,
                      ossUrl: attachment.file.oss_url || ''
                    }
                  });
                } else if (attachment.msgtype === 'link') {
                  formattedStep.messageList.push({
                    id: 'link-' + Date.now() + Math.random(),
                    type: 'LINK',
                    params: {
                      title: attachment.link.title || '',
                      url: attachment.link.url || '',
                      description: attachment.link.desc || '',
                      picUrl: attachment.link.picurl || '',
                      picUrl_ossUrl: attachment.link.pic_oss_url || ''
                    }
                  });
                } else if (attachment.msgtype === 'miniprogram') {
                  formattedStep.messageList.push({
                    id: 'miniprogram-' + Date.now() + Math.random(),
                    type: 'MINIPROGRAM',
                    params: {
                      title: attachment.miniprogram.title || '',
                      appid: attachment.miniprogram.appid || '',
                      pagepath: attachment.miniprogram.page || '',
                      thumbMediaId: attachment.miniprogram.pic_media_id || '',
                      thumbMediaId_ossUrl: attachment.miniprogram.pic_oss_url || ''
                    }
                  });
                }
              });
            }
          } else if (step.type === 'TAG') {
            try {
              const actionParams = formattedStep.actionParams;
              formattedStep.actionParams = {
                tagId: actionParams.tagId,
              };
              formattedStep.actionType = actionParams.operation === 'ADD' ? 'ADD_TAG' : 'REMOVE_TAG';
            } catch (e) {
              console.error('解析标签步骤actionParams失败:', e);
            }
          } else if (step.type === 'MOMENTS') {
            formattedStep.momentsList = [];
            
            // 处理文本内容
            if (formattedStep.actionParams.text && formattedStep.actionParams.text.content) {
              formattedStep.momentsList.push({
                id: 'moment-text-' + Date.now() + Math.random(),
                type: 'MOMENT_TEXT',
                params: {
                  content: formattedStep.actionParams.text.content
                }
              });
            }
            
            // 处理附件
            if (formattedStep.actionParams.attachments && Array.isArray(formattedStep.actionParams.attachments)) {
              formattedStep.actionParams.attachments.forEach(attachment => {
                if (attachment.msgtype === 'image') {
                  formattedStep.momentsList.push({
                    id: 'moment-image-' + Date.now() + Math.random(),
                    type: 'MOMENT_IMAGE',
                    params: {
                      mediaId: attachment.image.media_id,
                      ossUrl: attachment.image.oss_url || ''
                    }
                  });
                } else if (attachment.msgtype === 'video') {
                  formattedStep.momentsList.push({
                    id: 'moment-video-' + Date.now() + Math.random(),
                    type: 'MOMENT_VIDEO',
                    params: {
                      mediaId: attachment.video.media_id,
                      ossUrl: attachment.video.oss_url || ''
                    }
                  });
                } else if (attachment.msgtype === 'link') {
                  formattedStep.momentsList.push({
                    id: 'moment-link-' + Date.now() + Math.random(),
                    type: 'MOMENT_LINK',
                    params: {
                      title: attachment.link.title || '',
                      url: attachment.link.url || '',
                      description: attachment.link.desc || '',
                      picUrl: attachment.link.picurl || '',
                      picOssUrl: attachment.link.pic_oss_url || ''
                    }
                  });
                }
              });
            }
          }
          
          return formattedStep;
        });
        
        setSteps(formattedSteps);
      }
      
      // 检查是否有执行实例
      if (data.hasExecutions) {
        setHasExecutions(true);
      }
    } catch (error) {
      console.error('获取定时SOP模板详情失败:', error);
      message.error('获取模板详情失败');
    } finally {
      setLoading(false);
    }
  };

  // 判断模板是否为激活状态
  const isTemplateActive = () => {
    return template && template.status === SOP_CONSTANTS.TemplateStatus.ACTIVE;
  };

  return {
    template,
    setTemplate,
    loading,
    setLoading,
    editForm,
    fetchTemplateDetail,
    isTemplateActive
  };
};